// ClubLogoUpload Component
// Handles club logo upload to Cloudinary via backend

import React, { useState, useRef } from 'react';
import { FaCamera, FaSpinner, FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { auth } from '../../firebaseConfig';
import { useNotification } from '../../context/NotificationContext';
import styles from './ClubLogoUpload.module.css';

const ClubLogoUpload = ({ value, onChange }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const { showError, showSuccess } = useNotification();
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Get display URL from value
  const getDisplayUrl = () => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value.transformedUrls?.medium || value.url || null;
  };

  const displayUrl = getDisplayUrl();

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    try {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Logo must be less than 5MB');
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        throw new Error('Logo must be JPG, PNG, or WebP');
      }

      setIsUploading(true);
      setUploadProgress(10);

      // Create FormData
      const formData = new FormData();
      formData.append('clubLogo', file);

      // Get Firebase token
      const token = await auth.currentUser?.getIdToken();
      
      setUploadProgress(30);

      // Upload to backend
      const response = await fetch(`${backendUrl}/api/clubs/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      setUploadProgress(80);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload logo');
      }

      const data = await response.json();
      setUploadProgress(100);

      // Pass the full logo object to parent
      onChange(data.clubLogo || data);
      showSuccess('Logo uploaded successfully!');

    } catch (err) {
      showError(err.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  // Handle remove
  const handleRemove = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  // Handle click to upload
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={styles.container}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleInputChange}
        className={styles.hiddenInput}
      />

      <div
        className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''} ${displayUrl ? styles.hasImage : ''}`}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className={styles.uploadingState}>
            <FaSpinner className={styles.spinner} />
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill}
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className={styles.uploadingText}>Uploading... {uploadProgress}%</span>
          </div>
        ) : displayUrl ? (
          <div className={styles.previewContainer}>
            <img 
              src={displayUrl} 
              alt="Club logo preview" 
              className={styles.preview}
            />
            <div className={styles.previewOverlay}>
              <button 
                type="button"
                className={styles.changeButton}
                onClick={handleClick}
              >
                <FaCamera /> Change
              </button>
              <button 
                type="button"
                className={styles.removeButton}
                onClick={handleRemove}
              >
                <FaTimes /> Remove
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.iconContainer}>
              <FaCloudUploadAlt className={styles.uploadIcon} />
            </div>
            <div className={styles.uploadText}>
              <span className={styles.primaryText}>
                {dragActive ? 'Drop your logo here' : 'Click or drag to upload'}
              </span>
              <span className={styles.secondaryText}>
                JPG, PNG, or WebP • Max 5MB
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubLogoUpload;

